import { InputAdornment, styled, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import { FORTA_BLACK, FORTA_ERROR, FORTA_SUCCESS, FORTA_WHITE } from '../../utils/colors';
import { DEFAULT_INPUT_HEIGHT, Themes } from '../../constants';
import { getThemeColor } from '../../utils/common';

import type { TextFieldProps as MTextFieldProps } from '@mui/material';

type TextFieldProps = MTextFieldProps & {
  showStartAdornment?: boolean;
  showEndAdornment?: boolean;
  value?: string;
  colorTheme?: Themes;
  coloredBackground?: boolean;
  inputHeight?: number;
  placeholder?: string;
  error?: boolean;
  inputId?: string;
};

const StyledInputField = styled(TextField, {
  shouldForwardProp: (prop) =>
    prop !== 'colorTheme' &&
    prop !== 'coloredBackground' &&
    prop !== 'inputHeight' &&
    prop !== 'error',
})<TextFieldProps>(({ colorTheme, coloredBackground, inputHeight, sx = {}, error }) => ({
  backgroundColor: coloredBackground ? getThemeColor(colorTheme).light3 : FORTA_WHITE,
  borderRadius: '10px',
  overflow: 'hidden',
  boxShadow: `0px 4px 4px ${getThemeColor(colorTheme).light2}`,
  border: 'none',
  minWidth: 150,
  maxHeight: '100%',
  '&.MuiFormControl-root': {
    minHeight: inputHeight,
    border: error ? `2px solid ${FORTA_ERROR}` : 'none',
    '&:focus-within': {
      border: error ? `2px solid ${FORTA_ERROR}` : `2px solid ${FORTA_SUCCESS}`,
    },
  },

  '& .MuiInputBase-root': {
    color: FORTA_BLACK,
    flexDirection: 'row-reverse',
    padding: '0 8px',

    '&.Mui-disabled': {
      color: FORTA_BLACK,
      backgroundColor: coloredBackground ? getThemeColor(colorTheme).light3 : FORTA_WHITE,
    },

    '& .Mui-focused': {
      backgroundColor: `${getThemeColor(colorTheme).primary} !important`,
      color: FORTA_WHITE,
    },

    '& .MuiOutlinedInput-input': {
      minHeight: inputHeight,
      boxSizing: 'border-box',
      padding: '0.5em',

      '&.Mui-disabled': {
        WebkitTextFillColor: FORTA_BLACK,
      },
    },

    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },

    '&.MuiFilledInput-root': {
      background: 'transparent',
    },

    '&:before': {
      display: 'none',
    },
    '&:after': {
      display: 'none',
    },
    ...sx,
  },
}));

// To be fixed: rename to FHSearch
const FSearch = (props: TextFieldProps) => {
  const {
    showStartAdornment = true,
    showEndAdornment = false,
    colorTheme = Themes.Primary,
    coloredBackground = false,
    inputHeight = DEFAULT_INPUT_HEIGHT,
    placeholder = 'Search',
    inputId,
    ...rest
  } = props;
  return (
    <StyledInputField
      {...rest}
      colorTheme={colorTheme}
      coloredBackground={coloredBackground}
      inputHeight={inputHeight}
      variant="outlined"
      hiddenLabel
      placeholder={placeholder}
      InputProps={{
        id: inputId,
        startAdornment: showStartAdornment ? (
          <InputAdornment position="end">
            <SearchIcon sx={{ color: getThemeColor(colorTheme).primary }} />
          </InputAdornment>
        ) : null,
        endAdornment: showEndAdornment ? (
          <InputAdornment position="start">
            <SearchIcon sx={{ color: getThemeColor(colorTheme).primary }} />
          </InputAdornment>
        ) : null,
      }}
    />
  );
};

export default FSearch;
